import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import { useContactId } from "./useContactId";
import { toNumericId } from "../utils/toNumericId";
import { computeStockStatus } from "../utils/stockStatus";
import { resolveImageUrl } from "../utils/resolveImageUrl";

function normalizeCartItem(item) {
  const memberPrice = item?.memberPrice ?? item?.unitPrice ?? 0;
  const stockId =
    item?.stockId ?? item?.productId ?? item?.coreItemId ?? item?.cartItemId;

  return {
    ...item,
    id: toNumericId(item?.id ?? item?.cartItemId),
    stockId,
    title: item?.title ?? "",
    image: resolveImageUrl(item),
    imageUrl: resolveImageUrl(item),
    memberPrice,
    retailPrice: item?.retailPrice ?? null,
    quantity: item?.quantity ?? 1,
    color: item?.color ?? "N/A",
    size: item?.size ?? "N/A",
    isPreOrder: Boolean(item?.isPreOrder ?? item?.preOrder),
    ...computeStockStatus(item),
  };
}

function normalizeCart(cart) {
  const items = Array.isArray(cart?.items)
    ? cart.items.map(normalizeCartItem)
    : [];

  return {
    ...cart,
    id: cart?.id ?? cart?.cartId ?? "",
    cartId: cart?.cartId ?? cart?.id ?? "",
    items,
    total:
      cart?.total ??
      items.reduce((sum, item) => sum + item.memberPrice * item.quantity, 0),
  };
}

export function getCartItemCount(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items.reduce((sum, item) => sum + (item?.quantity ?? 1), 0);
}

function isCartShape(value) {
  return Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (Array.isArray(value.items) ||
        "cartId" in value ||
        "id" in value ||
        "total" in value)
  );
}

function selectCartByContact(cartResponse, contactId) {
  if (isCartShape(cartResponse)) return cartResponse;

  if (
    !cartResponse ||
    typeof cartResponse !== "object" ||
    Array.isArray(cartResponse)
  ) {
    return null;
  }

  const contactKey = String(contactId ?? "");
  if (contactKey && isCartShape(cartResponse[contactKey])) {
    return cartResponse[contactKey];
  }

  const keys = Object.keys(cartResponse);
  const sortedKeys = [...keys].sort((a, b) => Number(a) - Number(b));

  for (const key of sortedKeys) {
    if (isCartShape(cartResponse[key])) {
      return cartResponse[key];
    }
  }

  return null;
}

export function CartProvider({ children }) {
  return <>{children}</>;
}

export function useCart() {
  const contactId = useContactId();

  // Keyed by "cart" alone: the /cart request itself doesn't vary by
  // contactId (no query param sent), and contactId resolves asynchronously
  // via a separate /user/profile call. Including contactId in the query key
  // caused a spurious second fetch — the key changed from ["cart", null] to
  // ["cart", <id>] once the user profile loaded, even though the underlying
  // request was identical both times.
  return useQuery("cart", async () => {
    const res = await makeAuthedRequest("/cart");
    if (!res.ok) throw await parseApiError(res, "Cart fetch failed");
    const cartResponse = await res.json();
    const selectedCart = selectCartByContact(cartResponse, contactId);
    return normalizeCart(selectedCart ?? {});
  });
}

export function useAddToCart() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async (payload) => {
      const res = await makeAuthedRequest("/cart/items", {
        method: "POST",
        body: JSON.stringify({ ...payload, contactId }),
      });
      if (!res.ok) throw await parseApiError(res, "Add to cart failed");
      return res.json();
    },
    { onSuccess: () => queryClient.invalidateQueries("cart") }
  );
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();

  const applyOptimisticItemUpdate = (cart, itemId, payload) => {
    if (!cart || !Array.isArray(cart.items)) return cart;

    const updatedItems = cart.items.map((item) => {
      const matches =
        String(item?.id ?? "") === String(itemId ?? "") ||
        String(item?.cartItemId ?? "") === String(itemId ?? "");

      if (!matches) return item;

      return {
        ...item,
        quantity: payload?.quantity ?? item.quantity,
        color: payload?.color !== undefined ? payload.color : item.color,
        size: payload?.size !== undefined ? payload.size : item.size,
      };
    });

    return {
      ...cart,
      items: updatedItems,
    };
  };

  return useMutation(
    async ({ itemId, ...payload }) => {
      const res = await makeAuthedRequest(`/cart/items/${itemId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await parseApiError(res, "Update cart item failed");
      return res.json();
    },
    {
      onMutate: async (variables) => {
        const { itemId, ...payload } = variables || {};

        await queryClient.cancelQueries("cart");

        const previousCartQueries = queryClient.getQueriesData("cart");

        previousCartQueries.forEach(([queryKey, cart]) => {
          queryClient.setQueryData(
            queryKey,
            applyOptimisticItemUpdate(cart, itemId, payload)
          );
        });

        return { previousCartQueries };
      },
      onError: (_error, _variables, context) => {
        context?.previousCartQueries?.forEach(([queryKey, previousData]) => {
          queryClient.setQueryData(queryKey, previousData);
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries("cart");
      },
    }
  );
}

export function useRemoveFromCart() {
  const queryClient = useQueryClient();

  const applyOptimisticItemRemoval = (cart, itemId) => {
    if (!cart || !Array.isArray(cart.items)) return cart;

    const nextItems = cart.items.filter((item) => {
      const currentId = item?.id ?? item?.cartItemId;
      return String(currentId ?? "") !== String(itemId ?? "");
    });

    return {
      ...cart,
      items: nextItems,
    };
  };

  return useMutation(
    async (itemId) => {
      const res = await makeAuthedRequest(`/cart/items/${itemId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await parseApiError(res, "Remove from cart failed");
    },
    {
      onMutate: async (itemId) => {
        await queryClient.cancelQueries("cart");

        const previousCartQueries = queryClient.getQueriesData("cart");

        previousCartQueries.forEach(([queryKey, cart]) => {
          queryClient.setQueryData(
            queryKey,
            applyOptimisticItemRemoval(cart, itemId)
          );
        });

        return { previousCartQueries };
      },
      onError: (_error, _variables, context) => {
        context?.previousCartQueries?.forEach(([queryKey, previousData]) => {
          queryClient.setQueryData(queryKey, previousData);
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries("cart");
      },
    }
  );
}

// Identifies one logical "create a checkout session" intent. The server rejects a
// missing or malformed key, and replays of the same key return the original session
// instead of creating another, so a double-submit cannot open two sessions.
export function newIdempotencyKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  // Older Safari/Edge have getRandomValues but not randomUUID.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

export function useCheckout() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async (payload = {}) => {
      const { idempotencyKey, ...rest } = payload;
      if (!idempotencyKey) {
        throw new Error("Checkout failed: missing idempotency key");
      }

      // This goes through the wwdb-wwg-store-api BFF, which forwards only the
      // request body to func-bsm-api (no header passthrough), so the key must
      // travel in the body rather than as an Idempotency-Key header.
      const res = await makeAuthedRequest("/cart/checkout", {
        method: "POST",
        body: JSON.stringify({ ...rest, contactId, idempotencyKey }),
      });
      if (!res.ok) throw await parseApiError(res, "Checkout failed");
      return res.json();
    },
    {
      onSuccess: () => queryClient.invalidateQueries("cart"),
      // CartPage renders its own inline checkoutNotice for this flow.
      meta: { suppressErrorModal: true },
    }
  );
}

export function usePurchase() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async (payload = {}) => {
      const res = await makeAuthedRequest("/cart/purchase", {
        method: "POST",
        body: JSON.stringify({ ...payload, contactId }),
      });
      if (!res.ok) throw await parseApiError(res, "Purchase failed");
      return res.json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("cart");
        queryClient.invalidateQueries("orders");
      },
      // CartPage renders its own inline checkoutNotice for this flow.
      meta: { suppressErrorModal: true },
    }
  );
}
